import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';
import {ReferenceBookResponse} from '@npck/af-frontend-api';
import {PhoneNumberFormat, PhoneNumberType, PhoneNumberUtil} from 'google-libphonenumber';

export const kzPhoneInfo: PhoneInfo = {
    isoCode: 'KZ',
    countryName: 'КАЗАХСТАН',
    prefix: '+7',
    phoneMask: '0 000 000 0000',
};

const notSupportedPhoneByGoogleLib: PhoneInfo[] = [
    {isoCode: 'AB', countryName: 'АБХАЗИЯ', prefix: '+7', phoneMask: '0 000 0000000'},
    {isoCode: 'AQ', countryName: 'АНТАРКТИКА', prefix: '+672', phoneMask: '000 0 000 000'},
    {isoCode: 'UM', countryName: 'МЕЛКИЕ ОТДАЛЕННЫЕ ОСТРОВА США', prefix: '+1', phoneMask: '0 000 000 0000'},
    {isoCode: 'BV', countryName: 'ОСТРОВ БУВЕ', prefix: '+47', phoneMask: '00 000 00000'},
    {isoCode: 'PN', countryName: 'ПИТКЭРН', prefix: '+64', phoneMask: '00 00 000 0000'},
    {isoCode: 'TF', countryName: 'ФРАНЦУЗСКИЕ ЮЖНЫЕ ТЕРРИТОРИИ', prefix: '+262', phoneMask: '000 000 00 00 00'},
    {isoCode: 'HM', countryName: 'ХЕРД И МАКДОНАЛЬД (ОСТРОВА)', prefix: '+672', phoneMask: '000 0 000 000'},
    {isoCode: 'YU', countryName: 'ЮГОСЛАВИЯ', prefix: '+381', phoneMask: '000 00 000 000'},
    {isoCode: 'GS', countryName: 'ЮЖНАЯ ГЕОРГИЯ И ЮЖНЫЕ СЭНДВИЧЕВЫ ОСТРОВА', prefix: '+500', phoneMask: '000 00000'},
    {isoCode: 'OS', countryName: 'ЮЖНАЯ ОСЕТИЯ', prefix: '+995', phoneMask: '000 00 000 000'},
];

export function formatMsisdnDisplay(msisdn: string): string {
    if (!msisdn) {
        return msisdn;
    }
    return msisdn.startsWith('+') ? msisdn : `+${msisdn}`;
}

export function formatPhone(phone: string): string {
    const fixedPhone = normalizePhonePrefix(phone);
    const phoneUtil = PhoneNumberUtil.getInstance();
    const normalizedPhone = fixedPhone.startsWith('+') ? fixedPhone : `+${fixedPhone}`;
    return phoneUtil.format(phoneUtil.parse(normalizedPhone), PhoneNumberFormat.INTERNATIONAL);
}

export function getPhoneInfoByPhoneNumber(phone: string, phones: PhoneInfo[]): PhoneInfo {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const fixedPhone = normalizePhonePrefix(phone);
    const normalizedPhone = fixedPhone.startsWith('+') ? fixedPhone : `+${fixedPhone}`;
    const parsedPhone = phoneUtil.parse(normalizedPhone);
    const isoCode = phoneUtil.getRegionCodeForNumber(parsedPhone);
    return phones
        .find(item => item.isoCode === isoCode ||
            (item.prefix.includes(`+${parsedPhone.getCountryCode()}`) && item.phoneMask.replace(/\s/g, '').length === phone.length));
}

export function createPhoneList(countries: ReferenceBookResponse[]): PhoneInfo[] {
    const phoneUtil = PhoneNumberUtil.getInstance();
    return countries
        .filter(country => !['999', '-1'].includes(country.code))
        .map(country => {
                const exampleNumber = phoneUtil.getExampleNumberForType(country.code1, PhoneNumberType.MOBILE);
                let formattedNumber: string;
                try {
                    formattedNumber = phoneUtil.format(exampleNumber, PhoneNumberFormat.INTERNATIONAL);
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                } catch (e) {
                    formattedNumber = null;
                }
                if (formattedNumber !== null) {
                    return <PhoneInfo>{
                        isoCode: country.code1,
                        prefix: `+${exampleNumber?.getCountryCode()}`,
                        countryName: country.name_ru,
                        phoneMask: formattedNumber.replace('+', '')?.replace(/\d/g, '0'),
                    };
                } else {
                    return notSupportedPhoneByGoogleLib.find(item => item.isoCode === country.code1);
                }
            },
        );
}

const KZ_TOTAL_DIGITS = 11;
const KZ_PREFIX = '77';
const FOREIGN_MIN_DIGITS = 7;
const FOREIGN_MAX_DIGITS = 15;
const KZ_VALID_PREFIXES = new Set<string>([
    '700', '701', '702', '705', '706', '707', '708', '747', '771', '775', '776', '777', '778',
    '710', '711', '712', '713', '714', '715', '716', '717', '718',
    '721', '722', '723', '724', '725', '726', '727', '728', '729',
    '736',
]);
const SEQUENTIAL_MSISDN = '1234567890';

function isFakeNumber(digits: string): boolean {
    const local = digits.slice(-10);
    return new Set(local).size === 1 || local === SEQUENTIAL_MSISDN;
}

export function isValidMsisdn(msisdn: string): boolean {
    const digits = (msisdn ?? '').replace(/\D/g, '');
    if (!digits) {
        return false;
    }
    if (isFakeNumber(digits)) {
        return false;
    }
    if (digits.length === KZ_TOTAL_DIGITS && digits.startsWith(KZ_PREFIX)) {
        return KZ_VALID_PREFIXES.has(digits.substring(1, 4));
    }
    return digits.length >= FOREIGN_MIN_DIGITS && digits.length <= FOREIGN_MAX_DIGITS;
}

function trimmedControlValue(control: AbstractControl): string {
    return (control.value ?? '').toString().trim();
}

export function kzMsisdnValidator(getPrefix: () => string | null, isRequired: () => boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const raw = trimmedControlValue(control);
        if (!raw || raw === getPrefix()) {
            return isRequired() ? {required: true} : null;
        }
        return isValidMsisdn(raw) ? null : {mask: true};
    };
}

export function phoneMaskValidator(getPhoneInfo: () => PhoneInfo | null, isRequired: () => boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const raw = trimmedControlValue(control);
        const value = !raw || raw.startsWith('+') ? raw : `+${raw}`;
        const selectedPhoneInfo = getPhoneInfo();
        if (!selectedPhoneInfo) {
            return null;
        }
        const prefix = selectedPhoneInfo.prefix;
        const maskDigits = selectedPhoneInfo.phoneMask.replace(/\D/g, '');
        const expectedLength = `+${maskDigits}`.length;
        if (!value || value === prefix) {
            return isRequired() ? {required: true} : null;
        }
        if (value.length > prefix.length && value.length < expectedLength) {
            return {mask: true};
        }
        return null;
    };
}

export function getValidPhoneNumberOrNull(phoneNumber: string, selectedPhoneInfo: PhoneInfo): string | null {
    if (!phoneNumber) {
        return null;
    }
    const fixedPhone = normalizePhonePrefix(phoneNumber);
    const normalizedPhone = fixedPhone.startsWith('+') ? fixedPhone : `+${fixedPhone}`;
    const maskDigits = selectedPhoneInfo.phoneMask.replace(/\D/g, '');
    const expectedLength = `+${maskDigits}`.length;
    const isInvalidPhoneNumber = normalizedPhone.length < expectedLength;
    if (isInvalidPhoneNumber) {
        return null;
    } else {
        return normalizedPhone;
    }
}

export interface PhoneInfo {
    isoCode: string;
    prefix: string;
    phoneMask: string;
    countryName: string;
}

export function normalizePhonePrefix(phone: string): string {
    const raw = (phone ?? '').trim();
    return raw.replace(/^\+?0/, '+7');
}
